import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CrawlAttentionSection } from '@/components/admin/CrawlAttentionSection';
import { CrawlBatchRow } from '@/components/admin/CrawlBatchRow';
import { CrawlRunRow } from '@/components/admin/CrawlRunRow';
import { CrawlRunsFilterTabs } from '@/components/admin/CrawlRunsFilterTabs';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Layout } from '@/components/Layout';
import { type CrawlRun, useCrawlRuns, useRunCrawl } from '@/hooks/useCrawlRuns';
import {
  alertingRoasters,
  buildCrawlFeed,
  crawlBatches,
  crawlFilterCounts,
  type CrawlRunFilter,
} from '@/lib/crawlRuns';

const TOAST_DURATION_MS = 2600;

// Staff-only operational view of the nightly + manual crawler (design: Crawl
// Runs Admin). Rendering is gated by the is_admin claim in App.tsx — the real
// enforcement is the API's admin middleware, which 404s /admin/crawl/* for
// non-admins.
export function CrawlRunsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { runs, isLoading, error, refetch } = useCrawlRuns();
  const { runCrawl } = useRunCrawl();

  const [filter, setFilter] = useState<CrawlRunFilter>('all');
  // A just-triggered run to highlight — from the ?triggered=<runId> deep link
  // (arriving from the roaster inventory) or a re-run started here.
  const [highlightRunId, setHighlightRunId] = useState<string | null>(
    searchParams.get('triggered'),
  );
  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  function showToast(message: string) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(message);
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }

  const list = useMemo(() => runs ?? [], [runs]);
  const counts = useMemo(() => crawlFilterCounts(list), [list]);
  const alerting = useMemo(() => alertingRoasters(list), [list]);
  const groups = useMemo(() => buildCrawlFeed(list, filter), [list, filter]);

  // Glance line: the most recent cron pass, ok/failed at a glance.
  const lastCron = useMemo(
    () =>
      crawlBatches(list)
        .filter((batch) => batch.trigger === 'cron')
        .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())[0],
    [list],
  );

  // The crawl runs off-request now, so this ends at "queued" — the feed's own
  // polling reports what happens next.
  async function rerun(run: CrawlRun): Promise<void> {
    const result = await runCrawl(run.roasterId);
    if (!result.ok) {
      if (result.message) showToast(result.message);
      return;
    }
    setHighlightRunId(result.runId);
    setFilter('all');
    await refetch();
    showToast(`${run.roasterName} crawl queued`);
  }

  // Child rows expect a void-returning handler.
  const handleRerun = (run: CrawlRun): void => {
    void rerun(run);
  };

  return (
    <Layout>
      <div className="mx-auto max-w-[640px] px-4 pb-10" data-testid="crawl-runs-page">
        <div className="flex items-center gap-2 pb-1 pt-5">
          <button
            type="button"
            onClick={() => void navigate('/me')}
            aria-label="Back to account"
            data-testid="crawl-runs-back"
            className="-ml-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-wolisho-brown opacity-50 transition-opacity hover:opacity-80"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <span className="text-xs font-bold uppercase tracking-wider text-wolisho-brown/60">
            Admin
          </span>
        </div>

        <h1 className="mt-1 text-2xl font-bold text-black">Crawl runs</h1>

        {/* Both branches are gated on having nothing to show: a poll refetch
            flips isLoading and can fail, and neither should replace a feed
            already on screen — the next tick heals it. */}
        {isLoading && runs === null ? (
          <LoadingSpinner label="Loading crawl runs" testId="crawl-runs-loading" />
        ) : error && runs === null ? (
          <p className="mt-4 text-sm text-black/50" data-testid="crawl-runs-error">
            Couldn&apos;t load crawl runs. Refresh to try again.
          </p>
        ) : (
          <>
            <p className="mb-[18px] mt-0.5 text-sm text-black/50" data-testid="crawl-runs-subtitle">
              {lastCron ? (
                <>
                  Last crawl · {lastCron.roasterCount} roasters · {lastCron.okCount} ok
                  {lastCron.failedCount > 0 && (
                    <>
                      {' · '}
                      <span className="font-bold text-wolisho-red">{lastCron.failedCount} failed</span>
                    </>
                  )}
                </>
              ) : (
                'No scheduled crawls yet'
              )}
            </p>

            <CrawlAttentionSection alerting={alerting} onRerun={handleRerun} />

            <div className="mb-3">
              <CrawlRunsFilterTabs value={filter} onChange={setFilter} counts={counts} />
            </div>

            {groups.length > 0 ? (
              groups.map((group, groupIndex) => (
                <div key={group.label} className="mb-3.5">
                  <p className="mb-[7px] ml-0.5 text-[11px] font-bold uppercase tracking-[0.05em] text-wolisho-brown/40">
                    {group.label}
                  </p>
                  <div className="overflow-hidden rounded-xl bg-wolisho-card">
                    {group.entries.map((entry, index) => (
                      <Fragment
                        key={entry.type === 'batch' ? entry.batch.batchId : entry.run.id}
                      >
                        {index > 0 && <div className="mx-4 h-px bg-wolisho-brown/[0.06]" />}
                        {entry.type === 'batch' ? (
                          <CrawlBatchRow
                            batch={entry.batch}
                            onRerun={handleRerun}
                            defaultOpen={groupIndex === 0 && entry.batch.failedCount > 0}
                          />
                        ) : (
                          <CrawlRunRow
                            run={entry.run}
                            onRerun={handleRerun}
                            highlight={entry.run.id === highlightRunId}
                            defaultOpen={
                              entry.run.id === highlightRunId ||
                              (groupIndex === 0 && index === 0 && entry.run.status === 'failed')
                            }
                          />
                        )}
                      </Fragment>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div
                className="rounded-xl bg-wolisho-card px-4 py-11 text-center"
                data-testid="crawl-runs-empty"
              >
                <p className="mb-1 text-sm font-bold text-black/50">No runs here</p>
                <p className="text-[13px] text-black/35">Nothing matches this filter.</p>
              </div>
            )}
          </>
        )}
      </div>

      {toast && (
        <div
          className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-lg bg-wolisho-brown px-5 py-2.5 text-[13px] font-bold text-white"
          role="status"
          data-testid="crawl-runs-toast"
        >
          {toast}
        </div>
      )}
    </Layout>
  );
}
